import type { defaultSchema } from '@atlaskit/adf-schema/schema-default';
import type { NodeComponentsProps } from '@atlaskit/renderer/types';

import { BlockCard } from './nodes/BlockCard.tsx';
import { Blockquote } from './nodes/Blockquote.tsx';
import { BulletList } from './nodes/BulletList.tsx';
import { Caption } from './nodes/Caption.tsx';
import { CodeBlock } from './nodes/CodeBlock.tsx';
import { Date } from './nodes/Date.tsx';
import { DecisionItem } from './nodes/DecisionItem.tsx';
import { DecisionList } from './nodes/DecisionList.tsx';
import { EmbedCard } from './nodes/EmbedCard.tsx';
import { Emoji } from './nodes/Emoji.tsx';
import { HardBreak } from './nodes/HardBreak.tsx';
import { Heading } from './nodes/Heading.tsx';
import { InlineCard } from './nodes/InlineCard.tsx';
import { LayoutColumn } from './nodes/LayoutColumn.tsx';
import { LayoutSection } from './nodes/LayoutSection.tsx';
import { ListItem } from './nodes/ListItem.tsx';
import { Media } from './nodes/Media.tsx';
import { MediaGroup } from './nodes/MediaGroup.tsx';
import { MediaInline } from './nodes/MediaInline.tsx';
import { MediaSingle } from './nodes/MediaSingle.tsx';
import { Mention } from './nodes/Mention.tsx';
import { OrderedList } from './nodes/OrderedList.tsx';
import { Panel } from './nodes/Panel.tsx';
import { PanelC1 } from './nodes/PanelC1.tsx';
import { Paragraph } from './nodes/Paragraph.tsx';
import { Placeholder } from './nodes/Placeholder.tsx';
import { Rule } from './nodes/Rule.tsx';
import { Status } from './nodes/Status.tsx';
import { TaskItem } from './nodes/TaskItem.tsx';
import { TaskList } from './nodes/TaskList.tsx';
import { Table } from './nodes/table/Table.tsx';
import { TableCell, TableHeader } from './nodes/table/TableCell.tsx';
import { TableRow } from './nodes/table/TableRow.tsx';

export type LightNodeName = keyof Pick<
	typeof defaultSchema.nodes,
	| 'blockCard'
	| 'blockTaskItem'
	| 'blockquote'
	| 'bulletList'
	| 'caption'
	| 'codeBlock'
	| 'date'
	| 'decisionItem'
	| 'decisionList'
	| 'embedCard'
	| 'emoji'
	| 'hardBreak'
	| 'heading'
	| 'inlineCard'
	| 'layoutColumn'
	| 'layoutSection'
	| 'listItem'
	| 'media'
	| 'mediaGroup'
	| 'mediaInline'
	| 'mediaSingle'
	| 'mention'
	| 'orderedList'
	| 'panel'
	| 'panel_c1'
	| 'paragraph'
	| 'placeholder'
	| 'rule'
	| 'status'
	| 'table'
	| 'tableCell'
	| 'tableHeader'
	| 'tableRow'
	| 'taskItem'
	| 'taskList'
>;

export const lightNodeComponents: Pick<NodeComponentsProps, LightNodeName> = {
	blockCard: BlockCard,
	blockTaskItem: TaskItem,
	blockquote: Blockquote,
	bulletList: BulletList,
	caption: Caption,
	codeBlock: CodeBlock,
	date: Date,
	decisionItem: DecisionItem,
	decisionList: DecisionList,
	embedCard: EmbedCard,
	emoji: Emoji,
	hardBreak: HardBreak,
	heading: Heading,
	inlineCard: InlineCard,
	layoutColumn: LayoutColumn,
	layoutSection: LayoutSection,
	listItem: ListItem,
	media: Media,
	mediaGroup: MediaGroup,
	mediaInline: MediaInline,
	mediaSingle: MediaSingle,
	mention: Mention,
	orderedList: OrderedList,
	panel: Panel,
	panel_c1: PanelC1,
	paragraph: Paragraph,
	placeholder: Placeholder,
	rule: Rule,
	status: Status,
	table: Table,
	tableCell: TableCell,
	tableHeader: TableHeader,
	tableRow: TableRow,
	taskItem: TaskItem,
	taskList: TaskList,
};

export const lightSupportedNodeNames: readonly LightNodeName[] = Object.freeze(
	Object.keys(lightNodeComponents) as LightNodeName[],
);

// Table virtualization opts into these; without it, tables keep their subtree full.
export const lightTableNodeNames: readonly LightNodeName[] = Object.freeze([
	'table',
	'tableCell',
	'tableHeader',
	'tableRow',
]);
